if (!window.Eurus.loadedScript.has("personalisation-widget.js")) {
  window.Eurus.loadedScript.add("personalisation-widget.js");

  const personalisationJsonCache = new Map();

  function personalisationFetchJsonOnce(url) {
    let request = personalisationJsonCache.get(url);

    if (!request) {
      request = fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .catch((error) => {
          personalisationJsonCache.delete(url);
          throw error;
        });

      personalisationJsonCache.set(url, request);
    }

    return request.then((text) => JSON.parse(text));
  }

  function personalisationWithDeadline(promise, timeout) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(
        () => reject(new Error(`Timed out after ${timeout}ms`)),
        timeout
      );
      promise.then(
        (value) => {
          clearTimeout(timer);
          resolve(value);
        },
        (error) => {
          clearTimeout(timer);
          reject(error);
        }
      );
    });
  }

  function personalisationPimUrl(baseUrl, productId, currency) {
    return `${baseUrl}personalisation/${productId}?currency=${encodeURIComponent(currency)}`;
  }

  function personalisationCdnUrl(baseUrl, productId) {
    return `${baseUrl}configs/product-${productId}.json`;
  }

  document.addEventListener("alpine:init", () => {
    Alpine.data("xPersonalisation", () => ({
        productHasPersonalisation: false,
        pimEndpointUrl: null,
        cdnEndpointUrl: null,
        productId: null,
        productForm: null,
        addPersonalisationEnabled: true,
        loading: true,
        error: false,
        config: null,
        options: [],
        currency: null,
        selectedValues: {},
        totalPriceModifier: 0,
        formattedPriceModifier: "",
        iconCache: {},
        labels: {},
        validationErrors: {},
        moneyFormat: null,
        fontsReady: false,
        hasRequiredPersonalisation: false,
        showRequiredAlert: false,
        isFreePersonalisation: false,

        extractOptionsArray(data) {
          if (!data || typeof data !== "object") return [];
          const candidates = [
            data.options,
            data.data?.options,
            data.personalisation?.options,
            data.result?.options,
          ];
          for (const c of candidates) {
            if (Array.isArray(c)) return c;
          }
          return [];
        },

        // Keep first option when PIM sends duplicate keys
        dedupeOptionsByNormalizedKey(options) {
          if (!Array.isArray(options) || options.length === 0) {
            return { list: [], dropped: 0 };
          }
          const seen = new Set();
          const list = [];
          let dropped = 0;
          for (let i = 0; i < options.length; i++) {
            const opt = options[i];
            let k = opt?.key;
            if (k && typeof k === "string" && k.startsWith("_")) {
              k = k.substring(1);
            }
            if (k == null || k === "") {
              list.push(opt);
              continue;
            }
            const id = String(k);
            if (seen.has(id)) {
              dropped++;
              continue;
            }
            seen.add(id);
            list.push(opt);
          }
          return { list, dropped };
        },

        initialised: false,

        init() {
          if (this.initialised) return;
          this.initialised = true;

          this.pimEndpointUrl = this.$el.dataset.pimUrl;
          this.cdnEndpointUrl = this.$el.dataset.cdnUrl;
          this.productId = this.$el.dataset.productId;
          this.productForm = document.getElementById(this.$el.dataset.formId);
          this.moneyFormat = this.$el.dataset.moneyFormat;
          this.currency = this.$el.dataset.currency;
          this.locale = this.$el.dataset.locale;
          this.isFreePersonalisation = this.$el.dataset.freePersonalisation === 'true';
          this.errorMessages = this.$el.dataset.errorMessages ? JSON.parse(this.$el.dataset.errorMessages) : {};
          this.labels = {
            fontStyle: this.$el.dataset.labelFontStyle,
            fontColour: this.$el.dataset.labelFontColour,
          };

          if (!this.pimEndpointUrl && !this.cdnEndpointUrl || !this.productId) {
            this.loading = false;
            this.error = true;
            this.errorMessage =
              "There has been an error loading the personalisation widget";
            this.releaseAtcGate();
            return;
          }

          if (!this.productForm) {
            console.error(
              "Personalisation Widget: Product form not found - cannot initialise",
            );
            this.releaseAtcGate();
            return;
          }

          this.claimAtcGate();

          const personalisationDataPromise = this.fetchPersonalisationData();

          personalisationDataPromise
            .then(() => {
              if (this.options && this.options.length > 0) {
                
                this.productHasPersonalisation = true;
                
                this.hasRequiredPersonalisation = this.options.some(
                  (option) => option.required === true
                );
                
                if (this.hasRequiredPersonalisation || this.isFreePersonalisation) {
                  this.addPersonalisationEnabled = true;
                  if (this.$refs.addPersonalisationCheckbox) {
                    this.$refs.addPersonalisationCheckbox.checked = true;
                  }
                }

                this.$nextTick(() => {
                  if (this.hasRequiredPersonalisation && this.$refs.addPersonalisationCheckbox) {
                    this.$refs.addPersonalisationCheckbox.disabled = true;
                    this.$refs.addPersonalisationContainer?.classList.add(
                      "cursor-not-allowed",
                    );
                    this.$refs.addPersonalisationLabel?.classList.add(
                      "cursor-not-allowed",
                    );
                  }
                });
              }
            })
            .catch(() => {
              this.productHasPersonalisation = false;
            })
            .finally(() => {
              this.loading = false;
              this.releaseAtcGate();
            });

          this.$watch("selectedValues", (newVal, oldVal) => {
            // Clear validation error when user changes a field
            Object.keys(newVal).forEach((key) => {
              if (newVal[key] !== oldVal?.[key] && this.validationErrors[key]) {
                delete this.validationErrors[key];
              }
            });
            this.updateFormInputs();
          });

          this.$watch("addPersonalisationEnabled", () => {
            if (this.hasRequiredPersonalisation && !this.addPersonalisationEnabled) {
              this.addPersonalisationEnabled = true;
              if (this.$refs.addPersonalisationCheckbox) {
                this.$refs.addPersonalisationCheckbox.checked = true;
              }
              this.showRequiredAlert = true;
              setTimeout(() => {
                this.showRequiredAlert = false;
              }, 5000);
              return;
            }
            if (!this.addPersonalisationEnabled) {
              this.validationErrors = {};
            }
            this.updateFormInputs();
          });

          this.productForm.addEventListener("submit", (e) => {
            if (!this.validatePersonalisationForAddToCart()) {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          });

          // Add To Cart Button
          const atcButton = this.productForm.querySelector('button[type="submit"]');

          if (atcButton) {
            atcButton.addEventListener(
              "click",
              (e) => {
                if (!this.validatePersonalisationForAddToCart()) {
                  e.preventDefault();
                  e.stopImmediatePropagation();
                  return false;
                }
              },
              { capture: true }
            );
          }
        },

        handleTextInput(e, option){
              const key = option.key;
              const value = e.target.value;

              const sanitized = value.replace(/\p{Extended_Pictographic}/gu, "");

              if (sanitized !== value) {
                e.target.value = sanitized;

                this.selectedValues[key] = sanitized;

                this.validationErrors[key] = {
                  message: this.errorMessages.no_emojis || "You cannot use emojis in this field",
                  label: this.getOptionLabel(option)
                };

                return;
              }

              if (this.validationErrors[key]) {
                delete this.validationErrors[key];
              }
          },

        handleTextBlur(e, option) {
          const key = option.key;
          const trimmed = (e.target.value || "").trim();

          if (trimmed !== e.target.value) {
            e.target.value = trimmed;
            this.selectedValues[key] = trimmed;
          }

          this.dlOnPersonalisationBlur(e);
        },

        getFieldValue(key) {
          const value = this.selectedValues[key];
          return typeof value === "string" ? value.trim() : value;
        },

        async fetchPersonalisationData() {
          if (!this.productId) {
            this.loading = false;
            return;
          }

          // Try PIM first with 3-second timeout
          if (this.pimEndpointUrl) {
            try {
              const data = await this.fetchFromPIM();
              this.processPersonalisationData(data);
              this.loading = false;
              return;
            } catch (error) {
              console.warn('PIM fetch failed, falling back to CDN:', error);
            }
          }

          // Fallback to CDN
          if (this.cdnEndpointUrl) {
            try {
              const data = await this.fetchFromCDN();
              this.processPersonalisationData(data);
              this.loading = false;
            } catch (error) {
              console.error('CDN fetch failed:', error);
              this.error = true;
              this.errorMessage = "There has been an error loading the personalisation widget";
              this.loading = false;
            }
          } else {
            this.loading = false;
            this.error = true;
            this.errorMessage = "Both endpoints are not accessible";
          }
        },

        async fetchFromCDN() {
          const cdnData = await personalisationFetchJsonOnce(
            personalisationCdnUrl(this.cdnEndpointUrl, this.productId)
          );
          return this.transformCDNData(cdnData);
        },

        fetchFromPIM() {
          return personalisationWithDeadline(
            personalisationFetchJsonOnce(
              personalisationPimUrl(this.pimEndpointUrl, this.productId, this.currency)
            ),
            3000
          );
        },

        transformCDNData(cdnData) {
          const currencyData = cdnData.currencies?.[this.currency] || cdnData.currencies?.GBP;
          
          let translations = cdnData.translations?.[this.locale];
          
          // Fallback to language prefix match
          if (!translations && this.locale) {
            const localePrefix = this.locale.split('-')[0];
            const matchingKey = Object.keys(cdnData.translations || {}).find(key => 
              key.startsWith(localePrefix)
            );
            translations = matchingKey ? cdnData.translations[matchingKey] : null;
          }
          
          // Base locale
          translations = translations || 
                        cdnData.translations?.[cdnData.base_locale] || 
                        {};
          
          const options = [];

          // Transform text_lines
          if (cdnData.options?.text_lines) {
            cdnData.options.text_lines.forEach(textLine => {
              const translation = translations[textLine.label_key] || {};
              const priceModifier = this.calculatePriceForCurrency(
                textLine.price_gbp,
                cdnData.currencies,
                this.currency
              );
              
              options.push({
                type: textLine.type,
                label: translation.label || textLine.label_key,
                placeholder: translation.placeholder || '',
                meta_text: translation.meta_text || '',
                key: textLine.key,
                char_limit: textLine.char_limit,
                required: textLine.required,
                price_modifier: priceModifier,
                formatted_price: this.formatPrice(priceModifier, currencyData)
              });
            });
          }

          // Transform fonts
          if (cdnData.options?.fonts && cdnData.options.fonts.length > 0) {
            options.push({
              type: 'select',
              label: this.labels.fontStyle || 'Font',
              key: 'font',
              required: false,
              choices: cdnData.options.fonts.map(font => ({
                label: font.label,
                value: font.value,
                is_default: font.is_default || false,
                font_file_url: font.font_file_url
              }))
            });
          }

          // Transform font_colours
          if (cdnData.options?.font_colours && cdnData.options.font_colours.length > 0) {
            options.push({
              type: 'select',
              label: this.labels.fontColour || 'Font Colour',
              key: 'font_colour',
              required: false,
              choices: cdnData.options.font_colours.map(colour => ({
                label: colour.label,
                value: colour.value,
                hex: colour.hex,
                is_default: colour.is_default || false
              }))
            });
          }

          return {
            product_id: cdnData.product_id,
            locale: this.locale,
            currency: currencyData,
            options: options
          };
        },

        calculatePriceForCurrency(priceGBP, currencies, targetCurrency) {
          if (!priceGBP) return 0;
          const currencyData = currencies?.[targetCurrency];
          if (!currencyData) return priceGBP;
          return priceGBP * currencyData.exchange_rate_from_gbp;
        },

        formatPrice(amount, currencyData) {
          if (!amount || !currencyData) return '';
          
          const formattedAmount = amount.toFixed(currencyData.decimal_places)
            .replace('.', currencyData.decimal_separator)
            .replace(/\B(?=(\d{3})+(?!\d))/g, currencyData.thousands_separator);
          
          return currencyData.symbol_position === 'before'
            ? `${currencyData.symbol}${formattedAmount}`
            : `${formattedAmount}${currencyData.symbol}`;
        },

        processPersonalisationData(data) {
          const extracted = this.extractOptionsArray(data);
          const rawOpts = data?.options;

          if (extracted.length > 0) {
            const { list } = this.dedupeOptionsByNormalizedKey(extracted);
            this.options = list;
          } else if (rawOpts !== undefined && !Array.isArray(rawOpts)) {
            this.options = [];
          } else {
            this.options = [];
          }

          this.options.forEach((option, idx) => {
            // Normalize keys: remove leading underscore if present from API response
            if (option.key && option.key.startsWith('_')) {
              option.key = option.key.substring(1);
            }
            if (option.key == null || option.key === '') {
              option.key = `field_${idx}`;
            }
            option._alpineKey = `p${idx}_${String(option.key)}`;

            if (!Array.isArray(option.choices)) {
              option.choices = [];
            }

            // Auto-select single options or default choice
            if (option.choices.length === 1) {
              this.selectedValues[option.key] = option.choices[0].value;
            } else {
              const defaultChoice = option.choices.find(
                (choice) => choice.is_default
              );
              if (defaultChoice) {
                this.selectedValues[option.key] = defaultChoice.value;
              }
            }
          });

          this.injectFontStyles();
        },

        // ATC gate (see snippets/product-template.liquid). On bundle products the bundle
        // widget owns the button, so leave it alone there.
        atcGateButtons() {
          const formId = this.$el.dataset.formId;
          if (document.querySelector(`#engine-bundle-container[data-form-id="${formId}"]`)) return [];
          const scope = this.productForm || document;
          return Array.from(scope.querySelectorAll("[data-atc-gate]"));
        },

        claimAtcGate() {
          for (const btn of this.atcGateButtons()) {
            btn.dataset.atcGate = "active";
          }
        },

        releaseAtcGate() {
          for (const btn of this.atcGateButtons()) {
            const state = btn.dataset.atcGate;
            if (state !== "pending" && state !== "active") continue;
            btn.dataset.atcGate = "released";
            btn.disabled = false;
            btn.setAttribute("aria-disabled", "false");
          }
        },

        updateFormInputs() {
          if (!this.productForm) return;

          // Remove all existing personalisation inputs
          this.productForm
            .querySelectorAll("[data-personalisation-input]")
            .forEach((el) => el.remove());

          // Only add inputs if personalisation is enabled
          if (this.addPersonalisationEnabled || this.hasRequiredPersonalisation) {
            Object.keys(this.selectedValues).forEach((key) => {
              const value = this.getFieldValue(key);

              if (value) {
                // Find the option to get the label
                const option = this.options?.find((opt) => opt.key === key);
                let label = option ? this.getOptionLabel(option) : key;
                
                // Get user friendly value label (for select/radio options)
                const valueLabel = option ? this.getValueLabel(option, value) : value;

                // Ensure technical key has underscore prefix
                const technicalKey = key.startsWith('_') ? key : `_${key}`;

                // For single options, add underscore prefix to user friendly label name
                if (option && this.isSingleOption(option) && !label.startsWith('_')) {
                  label = `_${label}`;
                }

                // Store technical key (_font_colour = royal_blue)
                const technicalInput = document.createElement("input");
                technicalInput.type = "hidden";
                technicalInput.name = `properties[${technicalKey}]`;
                technicalInput.value = value;
                technicalInput.setAttribute("data-personalisation-input", "");
                this.productForm.appendChild(technicalInput);

                // Store user friendly label and value (Font Colour = Royal Blue)
                const labelInput = document.createElement("input");
                labelInput.type = "hidden";
                labelInput.name = `properties[${label}]`;
                labelInput.value = valueLabel;
                labelInput.setAttribute("data-personalisation-input", "");
                this.productForm.appendChild(labelInput);
              }
            });

            const appliedMarker = document.createElement("input");
            appliedMarker.type = "hidden";
            appliedMarker.name = "properties[_personalisation_applied]";
            appliedMarker.value = "1";
            appliedMarker.setAttribute("data-personalisation-input", "");
            this.productForm.appendChild(appliedMarker);

            // Add total price modifier for Shopify Functions (stored in cents for Shopify standard)
            const totalModifier = this.calculateTotalPriceModifier();
            if (totalModifier > 0) {
              const modifierInput = document.createElement("input");
              modifierInput.type = "hidden";
              modifierInput.name = "properties[_personalisation_total_modifier]";
              modifierInput.value = Math.round(totalModifier * 100);
              modifierInput.setAttribute("data-personalisation-input", "");
              this.productForm.appendChild(modifierInput);
            }
          }

          if (this._dlPersonalisationApplied === undefined) this._dlPersonalisationApplied = false;
          const stillApplied =
            (this.addPersonalisationEnabled || this.hasRequiredPersonalisation) &&
            Object.values(this.selectedValues).some((val) => val && String(val).trim().length > 1);
          if (this._dlPersonalisationApplied && !stillApplied) {
            this._dlPersonalisationApplied = false;
            this._dlPushPersonalisation("personalisation_removed");
          }
        },

        dlOnPersonalisationBlur(event) {
          const value = (event?.target?.value || "").trim();
          if (value.length > 1 && !this._dlPersonalisationApplied) {
            this._dlPersonalisationApplied = true;
            this._dlPushPersonalisation("personalisation_added");
          }
        },

        _dlPushPersonalisation(event) {
          const root = this.$root;
          if (!root) return;
          const price = parseFloat(root.dataset.dlPrice);
          const discount = parseFloat(root.dataset.dlDiscount);
          const data = {
            event: event,
            item_name: root.dataset.dlItemName || "",
            item_category: root.dataset.dlItemCategory || ""
          };
          if (Number.isFinite(price)) data.price = price;
          if (Number.isFinite(discount) && discount > 0) data.discount = discount;
          if (event === "personalisation_added") data.personalised = true;
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push(data);
        },

        calculateTotalPriceModifier() {
          if (this.isFreePersonalisation) {
            return 0;
          }
          let total = 0;
          if (!this.options || (!this.addPersonalisationEnabled && !this.hasRequiredPersonalisation)) {
            return 0;
          }
          this.options.forEach((option) => {
            if (this.getFieldValue(option.key) && option.price_modifier) {
              total += option.price_modifier;
            }
          });
          return total;
        },

        validatePersonalisation() {
          this.validationErrors = {};

          if (!this.addPersonalisationEnabled || !this.options) {
            return true;
          }

          let hasSomeTextFieldFilled = false

          this.options.forEach((option) => {
            const optionKey = option.key;
            const fieldValue = this.getFieldValue(optionKey)
            const fieldLabel = this.getOptionLabel(option);

            const isRequired = option.required;
            if(fieldValue && option.type === 'text') hasSomeTextFieldFilled = true;

            const isInvalid = isRequired && !fieldValue;

            if (isInvalid) {
              this.validationErrors[optionKey] = {
                message: this.errorMessages.field_required || "This field is required",
                label: fieldLabel
              };
            }
          });

          // in case no field was filled, add error message on all fields
          if (!hasSomeTextFieldFilled) {
            this.options.forEach((option) => {
              if(option.type !== 'text') return;
              
              this.validationErrors[option.key] = {
                message: this.errorMessages.some_field_required || "At least 1 field is required",
                label: this.getOptionLabel(option)
              };
            });
          }

          const allValid = Object.keys(this.validationErrors).length === 0;

          return allValid;
        },

        validatePersonalisationForAddToCart() {

          if (this.hasRequiredPersonalisation) {
            if (!this.addPersonalisationEnabled) {
              this.showRequiredAlert = true;

              setTimeout(() => {
                this.showRequiredAlert = false;
              }, 5000);

              this.$nextTick(() => {
                this.$el.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                });
              });

              return false;
            }

            const isValid = this.validatePersonalisation();

            if (!isValid) {
              this.$nextTick(() => {
                const firstErrorKey = Object.keys(this.validationErrors)[0];

                if (firstErrorKey) {
                  const errorField = this.$el.querySelector(`#${firstErrorKey}`);
                  if (errorField) {
                    errorField.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    });

                    errorField.focus();
                  } else {
                    this.$el.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    });
                  }
                }
              });
            }

            return isValid;
          }
          
          if (this.addPersonalisationEnabled) {
            const isValid = this.validatePersonalisation();

            if (!isValid) {
              this.$nextTick(() => {
                const firstErrorKey = Object.keys(this.validationErrors)[0];

                if (firstErrorKey) {
                  const errorField = this.$el.querySelector(`#${firstErrorKey}`);
                  if (errorField) {
                    errorField.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    });
                    
                    errorField.focus();
                  }
                }
              });
            }
            return isValid;
          }
          
          return true;
        },

        toggleAddPersonalisationEnabledCheckbox() {
          if (this.hasRequiredPersonalisation) {
            this.showRequiredAlert = true;
            setTimeout(() => {
              this.showRequiredAlert = false;
            }, 5000);
            return;
          }

          const checkbox = this.$refs.addPersonalisationCheckbox;

          if (checkbox && this.addPersonalisationEnabled) {
            checkbox.checked = false;
            this.$nextTick(() => {
              this.addPersonalisationEnabled = false;
            });
          } else {
            checkbox.checked = true;
            this.$nextTick(() => {
              this.addPersonalisationEnabled = true;
            });
          }
        },

        retrieveOptionTotalPriceModifier() {
          if (this.isFreePersonalisation) {
            return "";
          }
          let totalModifier = 0;
          if (!this.options || this.options.length === 0) {
            return "";
          }
          let optionsWithModifiers = this.options.filter(
            (option) => option.price_modifier
          );
          if (optionsWithModifiers.length === 1) {
            const formattedPrice = optionsWithModifiers[0].formatted_price;
            if (formattedPrice) {
              return `+${formattedPrice}`;
            } else {
              const price = optionsWithModifiers[0].price_modifier;
              return `+${Alpine.store("xHelper").formatMoney(price, this.moneyFormat)}`;
            }
          }
          optionsWithModifiers.forEach((option) => {
            if (option.price_modifier) {
              totalModifier += option.price_modifier;
            }
          });
          if (totalModifier > 0) {
            return `+${Alpine.store("xHelper").formatMoney(totalModifier * 100, this.moneyFormat)}`;
          }
          return "";
        },

        // Avoid template x-for inside select: browsers reorder DOM; Alpine loses vdom anchors.
        syncGenericSelectOptions(selectEl, option) {
          if (!selectEl || selectEl.tagName !== "SELECT") return;
          if (!option || option.key === "font" || option.key === "font_colour") {
            return;
          }
          const choices = Array.isArray(option.choices) ? option.choices : [];
          const sig = choices
            .map((c) => `${String(c?.value ?? "")}\t${String(c?.label ?? "")}`)
            .join("\n");
          if (selectEl.dataset.pwChoicesSig === sig) return;
          selectEl.dataset.pwChoicesSig = sig;
          const keepVal = selectEl.value;
          while (selectEl.options.length > 1) {
            selectEl.remove(1);
          }
          choices.forEach((choice) => {
            const o = document.createElement("option");
            const v = choice?.value != null ? String(choice.value) : "";
            o.value = v;
            o.textContent =
              choice?.label != null ? String(choice.label) : v || "—";
            selectEl.appendChild(o);
          });
          if (
            keepVal &&
            [...selectEl.options].some((opt) => opt.value === keepVal)
          ) {
            selectEl.value = keepVal;
          }
        },

        getOptionLabel(option) {
          if (option.key === "font") {
            return this.labels.fontStyle;
          } else if (option.key === "font_colour") {
            return this.labels.fontColour;
          } else {
            return option.label;
          }
        },

        getValueLabel(option, value) {
          // For text inputs, return the value as is
          if (option.type === 'text') {
            return value;
          }
          
          // For select/radio options, find the choice and return its label
          if (option.choices && Array.isArray(option.choices)) {
            const choice = option.choices.find((c) => c.value === value);
            return choice?.label || value;
          }
          
          // Fallback to value if no choice found
          return value;
        },

        getSelectedFontStyle() {
          const fontValue = this.selectedValues['font'];
          return fontValue ? `font-family: '${this.fontCssFamily(fontValue)}'` : '';
        },

        // Resolve the CSS font-family to render a font value with. Only webfonts are
        // ever shown, and their value can match a reserved CSS generic keyword (serif,
        // monospace, cursive, …) which cannot be registered as a FontFace or matched
        // as a custom family, so we namespace every family with a `pim-` prefix. The
        // raw value is still what gets stored on the line item; only rendering uses this.
        fontCssFamily(value) {
          return value ? `pim-${value}` : '';
        },

        getSelectedFontColourHex() {
          const colourValue = this.selectedValues['font_colour'];
          if (!colourValue || !this.options) return '';
          
          const fontColourOption = this.options.find(o => o.key === 'font_colour');
          if (!fontColourOption || !fontColourOption.choices) return colourValue;
          
          const selectedChoice = fontColourOption.choices.find(c => c.value === colourValue);
          return selectedChoice?.hex || colourValue;
        },

        async injectFontStyles() {
          const fontOption = this.options?.find(o => o.key === 'font');
          if (!fontOption?.choices) return;

          // Single shared promise ensures all fonts are loaded before any caller proceeds
          if (!window._personalisationFontsPromise) {
            window._personalisationFontsPromise = (async () => {
              for (const choice of fontOption.choices.filter(c => c.font_file_url)) {
                try {
                  const fontFace = new FontFace(
                    this.fontCssFamily(choice.value),
                    `url(${choice.font_file_url})`,
                    { display: 'swap' }
                  );
                  await fontFace.load();
                  document.fonts.add(fontFace);
                } catch (err) {
                  console.warn(`Failed to load font: ${choice.value}`, err);
                }
              }
            })();
          }

          await window._personalisationFontsPromise;

          // Auto-select the first available font only if the current selection
          // (e.g. the PIM default) is unavailable — i.e. it has no webfont file.
          const currentFont = this.selectedValues['font'];
          if (!currentFont || !this.isFontAvailable(currentFont)) {
            const firstVisibleFont = fontOption.choices.find(c => this.isFontAvailable(c.value));
            if (firstVisibleFont) {
              this.selectedValues['font'] = firstVisibleFont.value;
            }
          }

          this.fontsReady = true;
        },

        isFontAvailable(fontFamilyName) {
          // Only fonts that ship a webfont file are selectable. A font without a
          // file URL is hidden entirely — we do not fall back to a system font.
          const fontOption = this.options?.find(o => o.key === 'font');
          const choice = fontOption?.choices?.find(c => c.value === fontFamilyName);
          return !!(choice && choice.font_file_url);
        },
        isSingleOption(option) {
          return option.choices && option.choices.length === 1;
        },
        hasMultipleVisibleFonts() {
          // Show the selector only when more than one selectable (webfont) font
          // exists. Fonts without a file URL are hidden and don't count here.
          const fontOption = this.options?.find(o => o.key === 'font');
          if (!fontOption?.choices) return false;
          const visibleCount = fontOption.choices.filter(c => this.isFontAvailable(c.value)).length;
          return visibleCount > 1;
        },
        isWhiteFontColour() {
          const colourValue = this.selectedValues['font_colour'];
          if (!colourValue) return false;
          // Check if value name is "White"
          if (colourValue.toLowerCase() === 'white') return true;
          // Check choice label
          const fontColourOption = this.options?.find(o => o.key === 'font_colour');
          if (fontColourOption?.choices) {
            const selectedChoice = fontColourOption.choices.find(c => c.value === colourValue);
            if (selectedChoice?.label?.toLowerCase() === 'white') return true;
          }
          // Check hex code
          const hex = this.getSelectedFontColourHex();
          if (!hex) return false;
          const normalizedHex = hex.toLowerCase().replace('#', '');
          const whiteVariants = ['fff', 'ffffff', 'fefefe', 'fdfdfd', 'fcfcfc', 'fbfbfb', 'fafafa', 'f9f9f9', 'f8f8f8'];
          return whiteVariants.includes(normalizedHex);
        },
      }));
    });

  function readPersonalisationMarketSyncConfig() {
    const el = document.getElementById("personalisation-market-sync-config");
    if (!el || !el.textContent.trim()) return null;
    try {
      const cfg = JSON.parse(el.textContent);
      if (!cfg) return null;
      const f = cfg.isFreePersonalisation;
      cfg.isFreePersonalisation = f === true || f === "true" || f === 1;
      return cfg;
    } catch (_) {
      return null;
    }
  }

  async function personalisationFetchCartJson() {
    const root = window.Shopify?.routes?.root || "/";
    const res = await fetch(`${root}cart.js`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
    if (!res.ok) return null;
    return res.json();
  }

  function personalisationCalculatePriceForCurrency(priceGBP, currencies, targetCurrency) {
    if (!priceGBP) return 0;
    const currencyData = currencies?.[targetCurrency];
    if (!currencyData) return priceGBP;
    return priceGBP * currencyData.exchange_rate_from_gbp;
  }

  function personalisationTransformCDNData(cdnData, currency, locale) {
    let translations = cdnData.translations?.[locale];
    if (!translations && locale) {
      const localePrefix = locale.split("-")[0];
      const matchingKey = Object.keys(cdnData.translations || {}).find((key) =>
        key.startsWith(localePrefix)
      );
      translations = matchingKey ? cdnData.translations[matchingKey] : null;
    }
    translations = translations || cdnData.translations?.[cdnData.base_locale] || {};
    const options = [];
    if (cdnData.options?.text_lines) {
      cdnData.options.text_lines.forEach((textLine) => {
        const translation = translations[textLine.label_key] || {};
        const priceModifier = personalisationCalculatePriceForCurrency(
          textLine.price_gbp,
          cdnData.currencies,
          currency
        );
        options.push({
          type: textLine.type,
          label: translation.label || textLine.label_key,
          key: textLine.key,
          char_limit: textLine.char_limit,
          required: textLine.required,
          price_modifier: priceModifier,
        });
      });
    }
    if (cdnData.options?.fonts && cdnData.options.fonts.length > 0) {
      options.push({
        type: "select",
        key: "font",
        required: false,
        choices: cdnData.options.fonts.map((font) => ({
          label: font.label,
          value: font.value,
          is_default: font.is_default || false,
        })),
      });
    }
    if (cdnData.options?.font_colours && cdnData.options.font_colours.length > 0) {
      options.push({
        type: "select",
        key: "font_colour",
        required: false,
        choices: cdnData.options.font_colours.map((colour) => ({
          label: colour.label,
          value: colour.value,
          is_default: colour.is_default || false,
        })),
      });
    }
    return options;
  }

  async function personalisationFetchOptionsForProduct(productId, cfg) {
    if (cfg.pimUrl) {
      try {
        const data = await personalisationWithDeadline(
          personalisationFetchJsonOnce(
            personalisationPimUrl(cfg.pimUrl, productId, cfg.currency)
          ),
          3000
        );
        if (data.options && Array.isArray(data.options)) {
          data.options.forEach((option) => {
            if (option.key && option.key.startsWith("_")) {
              option.key = option.key.substring(1);
            }
          });
          return data.options;
        }
      } catch (_) {
        /* try CDN */
      }
    }
    if (cfg.cdnUrl) {
      try {
        const cdnData = await personalisationFetchJsonOnce(
          personalisationCdnUrl(cfg.cdnUrl, productId)
        );
        return personalisationTransformCDNData(cdnData, cfg.currency, cfg.locale);
      } catch (_) {
        return null;
      }
    }
    return null;
  }

  const personalisationOptionsCache = {};

  const PERSONALISATION_PROP_EXCLUDE = new Set([
    "_compare_at_price",
    "_compare_at_market",
    "_personalisation_total_modifier",
    "_personalisation_applied",
    "_gift_wrapping_id",
    "_key_link",
    "_customize_key_link",
  ]);

  function personalisationLineLooksPersonalised(props) {
    if (!props) return false;
    if (props._personalisation_applied === "1") return true;
    const fee = parseInt(String(props._personalisation_total_modifier || ""), 10);
    if (Number.isFinite(fee) && fee > 0) return true;
    if (props._font || props._font_colour) return true;
    return Object.keys(props).some(
      (k) =>
        k.startsWith("_") &&
        !PERSONALISATION_PROP_EXCLUDE.has(k) &&
        props[k] !== undefined &&
        props[k] !== ""
    );
  }

  function personalisationCartNeedsSyncForItem(props, cfg) {
    if (!personalisationLineLooksPersonalised(props)) return false;
    const fee = parseInt(String(props._personalisation_total_modifier || ""), 10);
    const normalized = Number.isFinite(fee) ? fee : 0;
    if (cfg.isFreePersonalisation) {
      return normalized > 0;
    }
    if (normalized > 0) return false;
    return (
      props._personalisation_applied === "1" ||
      props._font ||
      props._font_colour ||
      Object.keys(props).some(
        (k) =>
          k.startsWith("_") &&
          !PERSONALISATION_PROP_EXCLUDE.has(k) &&
          props[k] !== undefined &&
          props[k] !== ""
      )
    );
  }

  function personalisationComputeModifierCents(options, lineProps) {
    if (!options?.length || !lineProps) return 0;
    let total = 0;
    options.forEach((option) => {
      if (!option.price_modifier) return;
      let key = option.key;
      if (key && key.startsWith("_")) key = key.substring(1);
      const technicalKey = key.startsWith("_") ? key : `_${key}`;
      const val = lineProps[technicalKey] ?? lineProps[key];
      if (val) total += option.price_modifier;
    });
    return Math.round(total * 100);
  }

  function personalisationGetCartSectionArray() {
    try {
      if (
        typeof Alpine !== "undefined" &&
        typeof Alpine.store === "function" &&
        Alpine.store("xCartHelper")
      ) {
        return Alpine.store("xCartHelper").getSectionsToRender();
      }
    } catch (_) {
      /* xCartHelper not ready */
    }
    return [];
  }

  function personalisationMergeCartSectionsFromResponse(parsedState, sectionArray) {
    if (
      !parsedState?.sections ||
      !sectionArray?.length ||
      typeof getSectionInnerHTML !== "function"
    ) {
      return;
    }
    sectionArray.forEach((section) => {
      section.selector.split(",").forEach((selector) => {
        const sectionElement = document.querySelector(selector);
        if (sectionElement && parsedState.sections[section.id]) {
          sectionElement.innerHTML = getSectionInnerHTML(parsedState.sections[section.id], selector);
        }
      });
    });
  }

  let personalisationMarketSyncInFlight = false;

  async function personalisationCartMarketSync() {
    if (personalisationMarketSyncInFlight) {
      return;
    }
    personalisationMarketSyncInFlight = true;
    try {
      const cfg = readPersonalisationMarketSyncConfig();
      if (!cfg || (!cfg.pimUrl && !cfg.cdnUrl)) {
        return;
      }

      const root = window.Shopify?.routes?.root || "/";
      let changed = false;
      let guard = 0;

      while (guard++ < 20) {
        const cart = await personalisationFetchCartJson();
        if (!cart?.items?.length) break;

        const pending = cart.items.filter((item) =>
          personalisationCartNeedsSyncForItem(item.properties || {}, cfg)
        );
        if (!pending.length) break;

        let progressed = false;

        for (const candidate of pending) {
          const props = Object.assign({}, candidate.properties || {});

          let desiredCents = 0;
          if (cfg.isFreePersonalisation) {
            desiredCents = 0;
          } else {
            const pid = candidate.product_id;
            let opts = personalisationOptionsCache[pid];
            if (!opts) {
              try {
                opts = await personalisationFetchOptionsForProduct(String(pid), cfg);
              } catch (_) {
                opts = null;
              }
              if (Array.isArray(opts) && opts.length > 0) {
                personalisationOptionsCache[pid] = opts;
              }
            }
            if (!Array.isArray(opts) || opts.length === 0) {
              continue;
            }
            desiredCents = personalisationComputeModifierCents(opts, props);
          }

          const currentCents = parseInt(String(props._personalisation_total_modifier || ""), 10);
          const normalizedCurrent = Number.isFinite(currentCents) ? currentCents : 0;
          if (desiredCents === normalizedCurrent) {
            continue;
          }

          const nextProps = Object.assign({}, props);
          if (desiredCents <= 0) {
            nextProps._personalisation_total_modifier = "";
          } else {
            nextProps._personalisation_total_modifier = String(desiredCents);
          }

          const sectionArray = personalisationGetCartSectionArray();
          const changeBody = {
            id: candidate.key,
            quantity: candidate.quantity,
            properties: nextProps,
          };
          const sectionIds = sectionArray.map((s) => s.id);
          if (sectionIds.length > 0) {
            changeBody.sections = sectionIds;
            changeBody.sections_url = window.location.pathname;
          }

          try {
            const res = await fetch(`${root}cart/change.js`, {
              method: "POST",
              credentials: "same-origin",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              cache: "no-store",
              body: JSON.stringify(changeBody),
            });
            if (!res.ok) {
              continue;
            }
            const body = await res.json();
            if (body.status === 422) {
              continue;
            }
            personalisationMergeCartSectionsFromResponse(body, sectionArray);
            try {
              if (typeof Alpine !== "undefined" && Alpine.store("xCartHelper")?.updateEstimateShippingFull) {
                Alpine.store("xCartHelper").updateEstimateShippingFull();
              }
            } catch (_) {
              /* ignore */
            }
            changed = true;
            progressed = true;
            break;
          } catch (_) {
            continue;
          }
        }

        if (!progressed) {
          break;
        }
      }

      if (changed) {
        document.dispatchEvent(new CustomEvent("eurus:cart:items-changed"));
      }
    } finally {
      personalisationMarketSyncInFlight = false;
    }
  }

  function schedulePersonalisationCartMarketSync() {
    const run = () => {
      void personalisationCartMarketSync();
    };
    const delays = [0, 90, 260, 700, 1800];
    const planRuns = () => {
      delays.forEach((ms) => setTimeout(run, ms));
    };

    let tries = 0;
    const waitForThemeHelpers = () => {
      tries += 1;
      if (typeof getSectionInnerHTML === "function") {
        planRuns();
        return;
      }
      if (tries < 100) {
        setTimeout(waitForThemeHelpers, 30);
      } else {
        planRuns();
      }
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", waitForThemeHelpers, { once: true });
    } else {
      waitForThemeHelpers();
    }

    window.addEventListener(
      "load",
      () => {
        run();
        setTimeout(run, 120);
        setTimeout(run, 450);
      },
      { once: true }
    );
  }
  schedulePersonalisationCartMarketSync();
}
